import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import DiscordDraftSection from '../AddActivityWindow/DiscordDraftSection';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';

const SessionPopup = ({ session, onClose, onFileDownload }) => {
    const modalRef = useRef(null);
    const [expandFrom, setExpandFrom] = useState(false);
    const [expandTo, setExpandTo] = useState(false);
    const { groupMembers } = useAuth();
    useBodyScrollLock();
    const prMetaLabels = {
        title: "Title",
        body: "Description",
        additions: "Code Additions",
        deletions: "Code Deletions",
        commitCount: "Number of Commits",
        changedFiles: "Files Changed",
        collaborators: "Code Contributors",
        coAuthors: "Participants",
        state: "Status"
    };

    const googleDocMetaLabels = {
        title: "Title",
        wordCount: "Word Count",
        wordChange: "Word Changes",
        collaborators: "Contributors",
    };

    const figmaMetaLabels = {
        fileName: "File Name",
        fileType: "File Type",
        versionChange: "Version Change", // keep for nested keys
        versionStatus: "Version Status",
        versionCount: "Edit Count",
        editor: "Editor",
    };

    const filteredGoogleDocs = (session.googleDocs || []).filter(
        (doc) => doc && doc.link && doc.link.trim() !== ""
    );
    const filteredFigmaFiles = (session.figmaFiles || []).filter(
        (fig) => fig && fig.link && fig.link.trim() !== ""
    );

    const formatFigmaStatus = (status) => {
        switch (status) {
            case "no_change":
                return "Same";
            case "updated":
                return "New Version";
            case "initial":
                return "First Submission";
            default:
                return status || "Not fetched";
        }
    };

    const isEqual = (a, b) => {
        if (a === b) return true;
        if (typeof a !== typeof b) return false;

        if (Array.isArray(a) && Array.isArray(b)) {
            if (a.length !== b.length) return false;
            return a.every((val, i) => isEqual(val, b[i]));
        }

        if (typeof a === "object" && a && b) {
            const aKeys = Object.keys(a).sort();
            const bKeys = Object.keys(b).sort();
            if (aKeys.length !== bKeys.length) return false;
            return aKeys.every(k => isEqual(a[k], b[k]));
        }

        return false;
    };

    // Filter out placeholder docs/figmas (no valid link or meta)
    const filterValidFiles = (arr = []) => {
        return arr.filter(x => x && typeof x === "object" && x.link && x.link.trim() !== "");
    };


    const getEditorName = (email) => {
        const member = groupMembers.find(
            (m) => m.email?.toLowerCase() === email?.toLowerCase()
        );
        return member?.name || email; // fallback to email if no match
    };

    const fmtWordChange = (wc) => {
        if (!wc) return 'v1';
        const a = wc.added ?? 0, r = wc.removed ?? 0;
        return a === 0 && r === 0 ? 'same' : `+${a}/-${r}`;
    };


    const renderDocMeta = (meta = {}) => {
        const keysToCompare = ['wordCount', 'wordChange', 'collaborators'];

        return (
            <>
                {keysToCompare.map((k) => {
                    let displayValue = meta[k] ?? '—';

                    if (k === 'wordChange') {
                        displayValue = fmtWordChange(meta.wordChange);
                    } else if (k === 'collaborators') {
                        displayValue = Array.isArray(meta.collaborators)
                            ? meta.collaborators.map(x => x?.name || x).join(', ')
                            : (meta.collaborators ?? '—');
                    }

                    return (
                        <MetaRow key={k}>
                            <p>{googleDocMetaLabels[k] || k}:</p> {displayValue || '—'}
                        </MetaRow>
                    );
                })}
            </>
        );
    };

    const DocMetaStatic = ({ fromDoc, toDoc, changedMeta }) => {
        const fromPDF = fromDoc?.meta?.pdfStorage?.url;
        const toPDF = toDoc?.meta?.pdfStorage?.url;
        const fromLink = fromPDF || fromDoc?.link;
        const toLink = toPDF || toDoc?.link;
        const fromTitle = fromDoc?.meta?.title || fromDoc?.name || "Old Version";
        const toTitle = toDoc?.meta?.title || toDoc?.name || "New Version";
        return (
            <Container>
                {fromLink ? (
                    <TitleLink
                        href={fromLink}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {fromTitle}
                    </TitleLink>
                ) : (
                    <span style={{ color: "#999" }}>No previous file</span>
                )}
                {" → "}
                {toLink ? (
                    <TitleLink
                        href={toLink}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {toTitle}
                    </TitleLink>
                ) : (
                    <span style={{ color: "#999" }}>No new file</span>
                )}


                <PreviewBox $expanded>
                    {Object.entries(changedMeta || {}).map(([k, v]) => {
                        if (k === 'wordChange') {
                            return (
                                <MetaRow key={k}>
                                    <p>{googleDocMetaLabels[k] || k}:</p>{' '}
                                    {v.from} → {v.to}
                                </MetaRow>
                            );
                        }
                        if (k === 'collaborators') {
                            const fa = (v.from || []).map(x => x?.name || x).join(', ') || '∅';
                            const fb = (v.to || []).map(x => x?.name || x).join(', ') || '∅';
                            return (
                                <MetaRow key={k}>
                                    <p>{googleDocMetaLabels[k] || k}:</p> {fa} → {fb}
                                </MetaRow>
                            );
                        }
                        return (
                            <MetaRow key={k}>
                                <p>{googleDocMetaLabels[k] || k}:</p>{' '}
                                {(v.from ?? '∅')} → {(v.to ?? '∅')}
                            </MetaRow>
                        );
                    })}
                </PreviewBox>
            </Container>
        );
    };

    // A small, reusable item that shows a link and an expandable meta preview
    const DocMetaPreview = ({ doc }) => {
        const [open, setOpen] = React.useState(false);
        const meta = doc?.meta || {};
        return (
            <Container>
                <TitleLink href={meta?.pdfStorage?.url || doc.link} target="_blank" rel="noopener noreferrer">
                    {doc?.meta?.title || "Google Doc"}
                </TitleLink>

                <ToggleWrapper>
                    <ExpandToggle onClick={() => setOpen(o => !o)}>
                        <span>{open ? 'Hide details' : 'Show details'}</span>
                        {open ? <MdExpandLess /> : <MdExpandMore />}
                    </ExpandToggle>

                    {open && (
                        <PreviewBox $expanded={open}>
                            {renderDocMeta(meta)}
                        </PreviewBox>
                    )}
                </ToggleWrapper>
            </Container>
        );
    };

    const FigmaMetaPreview = ({ file }) => {
        const [open, setOpen] = React.useState(false);
        const meta = file?.meta || {};

        return (
            <Container>
                <TitleLink href={meta?.pdfStorage?.url || file.link} target="_blank">
                    {meta?.fileName || "Figma File"}
                </TitleLink>
                <ToggleWrapper>
                    <ExpandToggle onClick={() => setOpen(o => !o)}>
                        <span>{open ? "Hide details" : "Show details"}</span>
                        {open ? <MdExpandLess /> : <MdExpandMore />}
                    </ExpandToggle>

                    {open && (
                        <PreviewBox $expanded={open}>
                            {renderFigmaMeta(meta)}
                        </PreviewBox>
                    )}
                </ToggleWrapper>
            </Container>
        );
    };

    const renderFigmaMeta = (meta = {}) => {
        const keysToShow = ["fileName", "fileType", "versionStatus", "versionCount", "editor"];
        const versionStatus = formatFigmaStatus(meta?.versionChange?.status);
        const versionCount = meta?.versionChange?.count ?? 0;

        const displayMap = {
            fileName: meta?.fileName ?? "—",
            fileType: meta?.fileType ?? "—",
            versionStatus,
            versionCount: `${versionCount} edit${versionCount === 1 ? "" : "s"}`,
            editor: (() => {
                const editorsObj = meta?.editors || {};
                const editorNames = Object.keys(editorsObj);
                return editorNames.length > 0 ? editorNames.join(", ") : "—";
            })(),
        };


        return (
            <>
                {keysToShow.map((k) => (
                    <MetaRow key={k}>
                        <p>{figmaMetaLabels[k] || k}:</p> {displayMap[k]}
                    </MetaRow>
                ))}
            </>
        );
    };



    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose]);

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString();
    };

    const formatTime = (time) => {
        return time ? new Date(`1970-01-01T${time}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
    };

    const formatChange = (change) => {
        if (!change) return null;

        const changeDetails = [];

        Object.entries(change).forEach(([field, values], idx) => {
            switch (field) {
                case 'title':
                    changeDetails.push(<ChangeItem key={`title-${idx}`}>Title: <br />"{values.from}" → "{values.to}"</ChangeItem>);
                    break;
                case 'date':
                    changeDetails.push(<ChangeItem key={`date-${idx}`}>Date: {formatDate(values.from)} → {formatDate(values.to)}</ChangeItem>);
                    break;
                case 'startTime':
                    changeDetails.push(<ChangeItem key={`start-${idx}`}>Start Time: {formatTime(values.from)} → {formatTime(values.to)}</ChangeItem>);
                    break;
                case 'endTime':
                    changeDetails.push(<ChangeItem key={`end-${idx}`}>End Time: {formatTime(values.from)} → {formatTime(values.to)}</ChangeItem>);
                    break;
                case 'isCrossDate':
                    changeDetails.push(<ChangeItem key={`cross-${idx}`}>Cross Date: {values.from.toString()} → {values.to.toString()}</ChangeItem>);
                    break;
                case 'description':
                    changeDetails.push(<ChangeItem key={`desc-${idx}`}>Description:<br />"{values.from}" → "{values.to}"</ChangeItem>);
                    break;
                case 'labelColor':
                    changeDetails.push(<ChangeItem key={`label-${idx}`}>Label color: {values.from} → {values.to}</ChangeItem>);
                    break;
                case 'contributors':
                    const oldNames = values.from.map(c => c.name).join(', ');
                    const newNames = values.to.map(c => c.name).join(', ');
                    changeDetails.push(<ChangeItem key={`contrib-${idx}`}>Contributors:<br />{oldNames} → {newNames}</ChangeItem>);
                    break;
                case 'files':
                    if (values.from === values.to) return null; // skip identical names
                    changeDetails.push(
                        <ChangeItem key={`files-${idx}`}>
                            Files: {values.from} → {values.to}
                        </ChangeItem>
                    );
                    break;
                case 'prLink':
                    changeDetails.push(
                        <ChangeItem key={`prLink-${idx}`}>
                            PR Link: <br />
                            <a href={values.from} target="_blank" rel="noopener noreferrer">{values.from}</a> →{' '}
                            <a href={values.to} target="_blank" rel="noopener noreferrer">{values.to}</a>
                        </ChangeItem>
                    );
                    break;
                case 'prMeta':
                    Object.entries(values).forEach(([key, val], metaIdx) => {
                        const from = val.from;
                        const to = val.to;
                        const label = prMetaLabels[key] || key;
                        if (key === 'body') {
                            changeDetails.push(
                                <ChangeItem key={`prMeta-${idx}-${metaIdx}`}>
                                    PR Description:
                                    <div style={{ marginTop: '6px' }}>
                                        <PreviewBox $expanded={expandFrom}>
                                            {from}
                                        </PreviewBox>
                                        <ExpandToggle onClick={() => setExpandFrom(!expandFrom)}>
                                            {expandFrom ? 'Show less' : 'Show more'}
                                        </ExpandToggle>

                                    </div>

                                    <p style={{ margin: '6px 0' }}>→</p>

                                    <div style={{ marginTop: '6px' }}>
                                        <PreviewBox $expanded={expandTo}>
                                            {to}
                                        </PreviewBox>
                                        <ExpandToggle onClick={() => setExpandTo(!expandTo)}>
                                            {expandTo ? 'Show less' : 'Show more'}
                                        </ExpandToggle>

                                    </div>
                                </ChangeItem>
                            );
                        } else if (Array.isArray(from)) {
                            const fromNames = from.map(x => x?.name || x).join(', ');
                            const toNames = to.map(x => x?.name || x).join(', ');
                            changeDetails.push(
                                <ChangeItem key={`prMeta-${idx}-${metaIdx}`}>PR {label}:<br />{fromNames} → {toNames}</ChangeItem>
                            );
                        } else {
                            changeDetails.push(
                                <ChangeItem key={`prMeta-${idx}-${metaIdx}`}>PR {label}:<br />{from} → {to}</ChangeItem>
                            );
                        }
                    });
                    break;

                case 'googleDocs': {
                    const fromArr = filterValidFiles(values.from);
                    const toArr = filterValidFiles(values.to);

                    const mapByLink = (arr = []) => {
                        const m = new Map();
                        for (const d of arr) if (d?.link) m.set(d.link, d);
                        return m;
                    };

                    const fromMap = mapByLink(fromArr);
                    const toMap = mapByLink(toArr);

                    // compute added/removed by link
                    const added = [];
                    const removed = [];
                    for (const [link, toDoc] of toMap.entries()) if (!fromMap.has(link)) added.push(toDoc);
                    for (const [link, fromDoc] of fromMap.entries()) if (!toMap.has(link)) removed.push(fromDoc);

                    const oldLen = fromArr.length;
                    const newLen = toArr.length;

                    const fmtWordChange = (wc) => {
                        if (!wc) return 'Version 1';
                        const a = wc.added ?? 0, r = wc.removed ?? 0;
                        if (a === 0 && r === 0) return 'Same Doc';
                        return `+${a} / -${r}`;
                    };

                    // If any link set change (count change OR actual add/remove), show overall summary only.
                    if (oldLen !== newLen || added.length || removed.length) {
                        const delta = newLen - oldLen;
                        const deltaText = delta > 0 ? `(+${delta})` : delta < 0 ? `(${delta})` : '';

                        changeDetails.push(
                            <ChangeItem key="gdocs-count">
                                <SectionLabel>Number of Google Docs:</SectionLabel> {oldLen} → {newLen} {deltaText}
                                {(added.length || removed.length) && (
                                    <ChangeSection>
                                        {added.length > 0 && (
                                            <>
                                                <SectionLabel>Google Doc Added:</SectionLabel>
                                                <StyledList>
                                                    {added.map((d, i) => (
                                                        <DocMetaPreview key={`gadd-${i}`} doc={d} />
                                                    ))}
                                                </StyledList>
                                            </>
                                        )}
                                        {removed.length > 0 && (
                                            <>
                                                <SectionLabel>Google Doc Removed:</SectionLabel>
                                                <StyledList>
                                                    {removed.map((d, i) => (
                                                        <DocMetaPreview key={`grem-${i}`} doc={d} />
                                                    ))}
                                                </StyledList>
                                            </>
                                        )}
                                    </ChangeSection>
                                )}
                            </ChangeItem>
                        );
                        break;
                    }



                    // Links are the same set → compute per-doc meta changes only.
                    const keysToCompare = ['title', 'wordCount', 'collaborators', 'wordChange'];

                    const modified = [];
                    for (const [link, toDoc] of toMap.entries()) {
                        const fromDoc = fromMap.get(link) || {};
                        const fromMeta = fromDoc.meta || {};
                        const toMeta = toDoc.meta || {};

                        const changedMeta = {};
                        for (const k of keysToCompare) {

                            const a = fromMeta?.[k];
                            const b = toMeta?.[k];

                            if (k === 'wordChange') {
                                if (fmtWordChange(a) !== fmtWordChange(b)) {
                                    changedMeta[k] = { from: fmtWordChange(a), to: fmtWordChange(b) };
                                }
                                continue;
                            }
                            // normalize arrays (collaborators) for stable compare
                            const norm = (v) => Array.isArray(v) ? [...v] : v;
                            if (JSON.stringify(norm(a)) !== JSON.stringify(norm(b))) {
                                changedMeta[k] = { from: a, to: b };
                            }
                        }
                        if (Object.keys(changedMeta).length) {
                            modified.push({ link, fromDoc, toDoc, changedMeta });
                        }
                    }


                    if (modified.length > 0) {
                        changeDetails.push(
                            <ChangeItem key="gdocs-mods">
                                <SectionLabel>Google Doc Updated:</SectionLabel>
                                <ChangeSection>
                                    {modified.map((m, i) => (
                                        <DocMetaStatic
                                            key={m.link || i}
                                            fromDoc={m.fromDoc}
                                            toDoc={m.toDoc}
                                            changedMeta={m.changedMeta}
                                        />
                                    ))}
                                </ChangeSection>
                            </ChangeItem>
                        );
                    }
                    break;
                }

                case 'figmaFiles': {
                    const fromArr = filterValidFiles(values.from);
                    const toArr = filterValidFiles(values.to);

                    if (fromArr.length === 0 && toArr.length === 0) return;


                    const mapByLink = (arr = []) => {
                        const m = new Map();
                        for (const f of arr) if (f?.link) m.set(f.link, f);
                        return m;
                    };

                    const fromMap = mapByLink(fromArr);
                    const toMap = mapByLink(toArr);

                    const added = [];
                    const removed = [];
                    const modified = [];

                    // detect added / modified
                    for (const [link, toFile] of toMap.entries()) {
                        const fromFile = fromMap.get(link);
                        if (!fromFile) {
                            added.push(toFile);
                        } else if (!isEqual(fromFile.meta, toFile.meta)) {
                            const changedMeta = {};

                            const metaKeys = ['fileName', 'fileType', 'versionChange', 'editor'];

                            metaKeys.forEach(k => {
                                const a = fromFile.meta?.[k];
                                const b = toFile.meta?.[k];

                                // 🎨 split versionChange into two separate lines
                                if (k === 'versionChange') {
                                    const fromCount = a?.count ?? 'Not fetched';
                                    const toCount = b?.count ?? 'Not fetched';
                                    const fromStatus = a?.status ?? 'Not fetched';
                                    const toStatus = b?.status ?? 'Not fetched';

                                    if (fromStatus !== toStatus) {
                                        changedMeta['versionStatus'] = {
                                            from: formatFigmaStatus(fromStatus),
                                            to: formatFigmaStatus(toStatus),
                                        };
                                    }
                                    if (fromCount !== toCount) {
                                        changedMeta['versionCount'] = { from: fromCount, to: toCount };
                                    }
                                } else if (!isEqual(a, b)) {
                                    changedMeta[k] = { from: a, to: b };
                                }
                            });


                            if (Object.keys(changedMeta).length)
                                modified.push({ link, fromFile, toFile, changedMeta });
                        }
                    }

                    // detect removed
                    for (const [link, fromFile] of fromMap.entries()) {
                        if (!toMap.has(link)) removed.push(fromFile);
                    }

                    const oldLen = fromArr.length;
                    const newLen = toArr.length;

                    // case 1: number of links changed
                    if (oldLen !== newLen || added.length || removed.length) {
                        const delta = newLen - oldLen;
                        const deltaText = delta > 0 ? `(+${delta})` : delta < 0 ? `(${delta})` : '';
                        changeDetails.push(
                            <ChangeItem key="figma-count">
                                <SectionLabel>Number of Figma Files:</SectionLabel> {oldLen} → {newLen} {deltaText}
                                {(added.length || removed.length) && (
                                    <ChangeSection>
                                        {added.length > 0 && (
                                            <>
                                                <SectionLabel>Figma File Added:</SectionLabel>
                                                <StyledList>
                                                    {added.map((f, i) => (
                                                        <FigmaMetaPreview key={`fadd-${i}`} file={f} />
                                                    ))}
                                                </StyledList>
                                            </>
                                        )}
                                        {removed.length > 0 && (
                                            <>
                                                <SectionLabel>Figma File Removed:</SectionLabel>
                                                <StyledList>
                                                    {removed.map((f, i) => (
                                                        <FigmaMetaPreview key={`fadd-${i}`} file={f} />
                                                    ))}
                                                </StyledList>
                                            </>
                                        )}
                                    </ChangeSection>
                                )}
                            </ChangeItem>
                        );
                        break;
                    }

                    // case 2: same set of links, only meta changed
                    if (modified.length > 0) {
                        changeDetails.push(
                            <ChangeItem key="figma-mods">
                                <SectionLabel>Figma File Updated:</SectionLabel>
                                <ChangeSection>
                                    {modified.map((m, i) => {
                                        const fromPDF = m.fromFile?.meta?.pdfStorage?.url;
                                        const toPDF = m.toFile?.meta?.pdfStorage?.url;
                                        const fromLink = fromPDF || m.fromFile?.link;
                                        const toLink = toPDF || m.toFile?.link;
                                        const fromTitle = m.fromFile?.meta?.fileName || m.fromFile?.name || "Old Version";
                                        const toTitle = m.toFile?.meta?.fileName || m.toFile?.name || "New Version";
                                        return (
                                            <Container key={m.link || i}>
                                                {fromLink ? (
                                                    <TitleLink
                                                        href={fromLink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {fromTitle}
                                                    </TitleLink>
                                                ) : (
                                                    <span style={{ color: "#999" }}>No previous file</span>
                                                )}
                                                {" → "}
                                                {toLink ? (
                                                    <TitleLink
                                                        href={toLink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {toTitle}
                                                    </TitleLink>
                                                ) : (
                                                    <span style={{ color: "#999" }}>No new file</span>
                                                )}
                                                <PreviewBox $expanded>
                                                    {Object.entries(m.changedMeta).map(([k, v]) => (
                                                        <MetaRow key={k}>
                                                            <p>{figmaMetaLabels[k] || k}:</p>{' '}
                                                            {typeof v.from === 'object' || typeof v.to === 'object'
                                                                ? JSON.stringify(v.from, null, 0) + ' → ' + JSON.stringify(v.to, null, 0)
                                                                : `${v.from ?? 'Not fetched'} → ${v.to ?? 'Not fetched'}`}
                                                        </MetaRow>
                                                    ))}
                                                </PreviewBox>
                                            </Container>
                                        );
                                    })}
                                </ChangeSection>
                            </ChangeItem>
                        );
                    }
                    break;
                }
                default:
                    break;
            }
        });

        return changeDetails;
    };

    const modalContent = (
        <Overlay>
            <Content ref={modalRef}>
                <Header>
                    <Title>{session.title}</Title>
                    <CloseButton onClick={onClose}>×</CloseButton>
                </Header>
                <Body>
                    {session.description && (
                        <Section>
                            <SectionTitle>Description:</SectionTitle>
                            <Text>{session.description}</Text>
                        </Section>
                    )}


                    {session.files && session.files.length > 0 && (
                        <Section>
                            <SectionTitle>
                                File{session.files.length > 1 ? "s" : ""}:
                            </SectionTitle>
                            <FileList>
                                {session.files.map((file, index) => (
                                    <FileItem key={index}>
                                        <FileButton
                                            onClick={() => onFileDownload && onFileDownload(file.url, file.name)}
                                        >
                                            {file.name}
                                        </FileButton>
                                    </FileItem>
                                ))}
                            </FileList>
                        </Section>
                    )}

                    {session.prLink && session.prMeta && (
                        <Section>
                            <SectionTitle>Pull Request:</SectionTitle>
                            <Text>
                                <PRLink href={session.prLink} target="_blank" rel="noopener noreferrer">
                                    {session.prMeta.title}
                                </PRLink>
                            </Text>
                            <PRMeta>
                                {session.prMeta.changedFiles} file{session.prMeta.changedFiles !== 1 ? "s" : ""} ·{" "}
                                <AdditionText>+{session.prMeta.additions}</AdditionText> / <DeletionText>-{session.prMeta.deletions}</DeletionText> ·{" "}
                                {session.prMeta.commitCount} commit{session.prMeta.commitCount !== 1 ? "s" : ""}
                            </PRMeta>
                        </Section>
                    )}

                    {filteredGoogleDocs && filteredGoogleDocs.length > 0 && (
                        <Section>
                            <SectionTitle>
                                Google Doc{filteredGoogleDocs.length > 1 ? "s" : ""}:
                            </SectionTitle>
                            {filteredGoogleDocs.map((doc, idx) => {
                                const hasMeta = doc?.meta && typeof doc.meta === "object";
                                const meta = hasMeta ? doc.meta : null;
                                const key = doc.link || doc.meta?.title || `google-${idx}`;

                                return (
                                    <GoogleDocItem key={key}>
                                        <Text>
                                            <PRLink href={hasMeta ? (meta?.pdfStorage?.url || doc?.link) : doc?.link} target="_blank" rel="noopener noreferrer" $isRed={!hasMeta}>
                                                {hasMeta
                                                    ? meta.title || "View Document"
                                                    : doc?.name || "Google Doc Link"}
                                            </PRLink>
                                        </Text>
                                        {hasMeta ? (
                                            <PRMeta>
                                                {meta.wordChange ? (
                                                    meta.wordChange.added === 0 && meta.wordChange.removed === 0 ? (
                                                        <span>Same Document</span>
                                                    ) : (
                                                        <span>
                                                            <AdditionText>+{meta.wordChange.added}</AdditionText> /{" "}
                                                            <DeletionText>-{meta.wordChange.removed}</DeletionText>
                                                        </span>
                                                    )
                                                ) : (
                                                    <span>Initial Version</span>
                                                )}{" "}
                                                · {meta.wordCount} word{meta.wordCount !== 1 ? "s" : ""}
                                            </PRMeta>
                                        ) : (
                                            <MissingMeta>Metadata not fetched -- fetch required</MissingMeta>
                                        )}
                                    </GoogleDocItem>
                                );
                            })}
                        </Section>
                    )}

                    {filteredFigmaFiles && filteredFigmaFiles.length > 0 && (
                        <Section>
                            <SectionTitle>
                                Figma File{filteredFigmaFiles.length > 1 ? "s" : ""}:
                            </SectionTitle>

                            {filteredFigmaFiles.map((fig, idx) => {
                                const hasMeta = fig?.meta && typeof fig.meta === "object";
                                const meta = hasMeta ? fig.meta : null;
                                const key = fig.link || fig.meta?.fileName || `figma-${idx}`;

                                return (
                                    <FigmaItem key={key}>
                                        <Text>
                                            <PRLink
                                                href={hasMeta ? (meta?.pdfStorage?.url || fig?.link) : fig?.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                $isRed={!hasMeta}
                                            >
                                                {meta?.fileName || "Figma File"}
                                            </PRLink>
                                        </Text>

                                        {hasMeta ? (
                                            <>
                                                <PRMeta>
                                                    {meta.versionChange?.count ?? 0} edit
                                                    {meta.versionChange?.count !== 1 ? "s" : ""} ·{" "}
                                                    {meta.versionChange?.status === "initial" && "First submission"}
                                                    {meta.versionChange?.status === "no_change" && "Same"}
                                                    {meta.versionChange?.status === "updated" &&
                                                        `New version${meta.versionChange?.count > 1 ? "s" : ""}`} ·{" "}
                                                    {meta.fileType
                                                        ? `${meta.fileType.charAt(0).toUpperCase() + meta.fileType.slice(1)}`
                                                        : "Unknown"}

                                                </PRMeta>

                                                {meta.pdfStorage?.thumbnailUrl && (
                                                    <FigmaPreviewWrapper>
                                                        <FigmaPreview
                                                            src={meta.pdfStorage.thumbnailUrl}
                                                            alt={meta.fileName || "Figma Preview"}
                                                        />
                                                    </FigmaPreviewWrapper>
                                                )}
                                            </>
                                        ) : (
                                            <MissingMeta>Metadata not fetched -- fetch required</MissingMeta>
                                        )}
                                    </FigmaItem>
                                );
                            })}
                        </Section>
                    )}

                    {session.discord?.messages?.length > 0 && (
                        <Section>
                            <SectionTitle>Discord chat:</SectionTitle>
                            <DiscordDraftSection
                                discordDraft={session.discord}
                                maxHeight={220}
                                selectable={false}
                                groupMembers={groupMembers}
                            />
                        </Section>
                    )}

                    {session.editHistory && session.editHistory.length > 0 && (
                        <Section>
                            <SectionTitle>Edit History:</SectionTitle>
                            <HistoryList>
                                {session.editHistory.map((edit, index) => {
                                    const key = edit.timestamp?.seconds || `edit-${index}`;
                                    return (
                                        <HistoryItem key={key}>
                                            <HistoryHeader>
                                                <TimeStamp>
                                                    {new Date(edit.timestamp.toDate()).toLocaleString()}
                                                </TimeStamp>
                                                <Editor>by {getEditorName(edit.editor)}</Editor>
                                            </HistoryHeader>
                                            <Reason>Reason: {edit.reason}</Reason>
                                            <Changes>{formatChange(edit.changes)}</Changes>
                                        </HistoryItem>
                                    );
                                })}
                            </HistoryList>
                        </Section>
                    )}

                </Body>
            </Content>
        </Overlay>
    );

    return ReactDOM.createPortal(modalContent, document.body);
};

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const Content = styled.div`
    background-color: white;
    border-radius: 8px;
    width: 90%;
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    position: relative;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 8px;
        background-color: #9333ea;
        border-radius: 8px 8px 0 0;
    }
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    padding-top: 28px;
    border-bottom: 1px solid #e0e0e0;
`;

const Title = styled.h2`
    margin: 0;
    font-size: 1.5rem;
    color: #333;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #666;
    padding: 0 5px;
    line-height: 1;

    &:hover {
        color: #333;
    }
`;

const Body = styled.div`
    padding: 20px;
`;

const Section = styled.div`
    margin-bottom: 20px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const SectionTitle = styled.h3`
    font-size: 1.1rem;
    margin-bottom: 10px;
    color: #333;
`;

const Text = styled.p`
    font-size: 0.9rem;
    line-height: 1.5;
    color: #444;
`;

const HistoryList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
`;

const HistoryItem = styled.div`
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 12px;
    background-color: #f8f9fa;
`;

const HistoryHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
`;

const TimeStamp = styled.span`
    font-size: 0.85rem;
    color: #666;
`;

const Editor = styled.span`
    font-size: 0.85rem;
    color: #666;
`;

const Reason = styled.div`
    font-size: 0.9rem;
    margin-bottom: 8px;
    font-style: italic;
    color: #444;
`;

const Changes = styled.pre`
    font-size: 0.9rem;
    white-space: pre-wrap;
    margin: 0;
    padding: 8px;
    background-color: #fff;
    border-radius: 4px;
    border: 1px solid #eee;
    color: #333;

    
  a {
    color: #2563eb;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }

`;

const FileList = styled.ul`
    list-style-type: none;
    padding: 0;
    margin: 0;
`;

const FileItem = styled.li`
    margin-bottom: 5px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const FileButton = styled.button`
    background: none;
    border: none;
    color: #0066cc;
    text-decoration: underline;
    cursor: pointer;
    font-size: 0.9rem;
    padding: 0;

    &:hover {
        color: #004499;
    }
`;

const PRLink = styled.a`
  color: ${(p) => (p.$isRed ? "#dc2626" : "#1a73e8")};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
    color: ${(p) => (p.$isRed ? "#b91c1c" : "#0c52d0")};
  }
`;

const MissingMeta = styled.div`
  color: #b91c1c;
  font-size: 13px;
  font-style: italic;
  margin-top: 2px;
`;

const PRMeta = styled.div`
  font-size: 0.875rem;
  color: #4b5563;
  margin-top: 0.25rem;
`;

const AdditionText = styled.span`
  color: #16a34a;
`;

const DeletionText = styled.span`
  color: #dc2626;
`;

const ChangeItem = styled.div`
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0; /* remove extra space after last item */
  }
`;

const PreviewBox = styled.div`
  margin-top: 4px;
  background-color: #f9f9f9;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  white-space: pre-wrap;
  font-size: 0.9rem;
  color: #333;
  max-height: ${({ $expanded }) => ($expanded ? 'none' : '100px')};
  overflow: hidden;
  position: relative;
`;

const ExpandToggle = styled.div`
  display: flex;
  align-items: center;
  color: #2563eb; /* Tailwind text-gray-600 */
  cursor: pointer;
  font-size: 0.875rem; /* text-sm */
  user-select: none;

  span {
    display: flex;
    align-items: center;
    font-size: 0.8rem; /* text-lg */
  }

  &:hover {
    text-decoration: underline;
  }
`;

const GoogleDocItem = styled.div`
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0; /* remove extra space after last item */
  }
`;

const MetaRow = styled.div`
  margin-bottom: 4px;

  p {
    display: inline;
    margin-right: 4px;
    font-weight: 400;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const TitleLink = styled.a`
  color: #2563eb; /* Tailwind blue-600 */
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const Container = styled.div`
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0; /* remove extra space after last item */
  }
`;

const ToggleWrapper = styled.div`
  margin-top: 2px;
`;

const ChangeSection = styled.div`
  margin-top: 6px;
`;
const StyledList = styled.ul`
  margin: 4px 0;
`;

const SectionLabel = styled.p`
  margin: 0 0 4px 0;
`;

const FigmaItem = styled.div`
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FigmaPreviewWrapper = styled.div`
  margin-top: 6px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
  max-height: 300px;
  max-width: 100%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
`;

const FigmaPreview = styled.img`
  width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
  background-color: #fafafa;
`;



export default SessionPopup;
